import { useState, type ReactNode } from 'react';
import { Archive, ArchiveRestore, ArrowUp, Plus, Power, PowerOff, Users, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { ProductListItem } from '@/types/product';
import { ROW_STATUS, summarizeSelection } from './productListView';

interface BulkActionBarProps {
  products: ProductListItem[];
  selected: Set<string>;
  labels: string[];
  busy: boolean;
  onPublish: () => void;
  onSetEnabled: (enabled: boolean) => void;
  onSetArchived: (archived: boolean) => void;
  onAddLabel: (label: string) => void;
  onReviewers: () => void;
  onClear: () => void;
}

/**
 * Verschijnt pas bij een selectie en vertelt eerst wát je vast hebt. De acties
 * passen zich aan de selectie aan: een selectie die al helemaal aan staat krijgt
 * "uitschakelen", niet een knop die niets doet.
 */
export function BulkActionBar({
  products,
  selected,
  labels,
  busy,
  onPublish,
  onSetEnabled,
  onSetArchived,
  onAddLabel,
  onReviewers,
  onClear,
}: BulkActionBarProps) {
  const [newLabel, setNewLabel] = useState('');

  const chosen = products.filter((product) => selected.has(product.id));
  if (!chosen.length) return null;

  const summary = summarizeSelection(products, selected);
  const publishable = chosen.filter((product) => product.hasDraft && !product.archivedAt).length;
  const allEnabled = chosen.every((product) => product.enabled);
  const allArchived = chosen.every((product) => product.archivedAt);

  const submitLabel = (label: string) => {
    const trimmed = label.trim();
    if (!trimmed) return;
    onAddLabel(trimmed);
    setNewLabel('');
  };

  return (
    <>
      {/* De balk zweeft, dus hij duwt niets opzij. Deze ruimte houdt de laatste
          productrijen vrij, zodat je niet net het product mist waar je op klikte. */}
      <div aria-hidden className="h-[76px]" />

      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center pb-6">
        <div className="pointer-events-auto flex h-[52px] items-center gap-3.5 rounded-md bg-[#2B3D4F] pl-[18px] pr-2.5 shadow-[0_8px_24px_rgba(43,61,79,0.28)]">
          <div className="flex items-center gap-2.5">
            <span className="text-sm font-semibold text-white">{chosen.length} geselecteerd</span>
            <span className="text-[13px] text-[#A8B6C4]">
              {summary
                .map(({ status, count }) => `${count} ${ROW_STATUS[status].label.toLowerCase()}`)
                .join(' · ')}
            </span>
          </div>

          <div className="h-6 w-px bg-white/20" />

          <button
            type="button"
            onClick={onPublish}
            disabled={busy || publishable === 0}
            title={
              publishable === 0
                ? 'Geen van de gekozen producten heeft een concept om te publiceren'
                : undefined
            }
            className="flex h-[34px] items-center gap-2 rounded bg-[#00856A] px-3.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            <ArrowUp className="h-[15px] w-[15px]" />
            Publiceren
            {publishable > 0 && publishable !== chosen.length && (
              <span className="font-normal text-white/70">{publishable}</span>
            )}
          </button>

          <BarButton onClick={() => onSetEnabled(!allEnabled)} disabled={busy || allArchived}>
            {allEnabled ? (
              <PowerOff className="h-[15px] w-[15px]" />
            ) : (
              <Power className="h-[15px] w-[15px]" />
            )}
            {allEnabled ? 'Uitschakelen' : 'Inschakelen'}
          </BarButton>

          <BarButton onClick={onReviewers} disabled={busy}>
            <Users className="h-[15px] w-[15px]" />
            Reviewers
          </BarButton>

          <DropdownMenu>
            <DropdownMenuTrigger
              disabled={busy}
              className="flex h-[34px] items-center gap-2 rounded border border-white/25 px-3.5 text-sm text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-40"
            >
              <Plus className="h-[15px] w-[15px]" />
              Label toevoegen
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" side="top" className="w-64">
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                Voegt het label toe aan alle {chosen.length} gekozen producten
              </DropdownMenuLabel>
              <div className="p-1">
                <Input
                  value={newLabel}
                  placeholder="Nieuw label…"
                  onChange={(event) => setNewLabel(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') return;
                    event.preventDefault();
                    submitLabel(newLabel);
                  }}
                />
              </div>
              {labels.length > 0 && <DropdownMenuSeparator />}
              <div className="max-h-56 overflow-y-auto">
                {labels.map((label) => (
                  <DropdownMenuItem key={label} onSelect={() => submitLabel(label)}>
                    {label}
                  </DropdownMenuItem>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <BarButton onClick={() => onSetArchived(!allArchived)} disabled={busy}>
            {allArchived ? (
              <ArchiveRestore className="h-[15px] w-[15px]" />
            ) : (
              <Archive className="h-[15px] w-[15px]" />
            )}
            {allArchived ? 'Uit archief' : 'Archiveren'}
          </BarButton>

          <button
            type="button"
            onClick={onClear}
            aria-label="Selectie wissen"
            className="flex h-[34px] w-[34px] items-center justify-center rounded text-[#A8B6C4] transition-colors hover:bg-white/10 hover:text-white"
          >
            <X className="h-[17px] w-[17px]" />
          </button>
        </div>
      </div>
    </>
  );
}

function BarButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex h-[34px] items-center gap-2 rounded border border-white/25 px-3.5 text-sm text-white transition-colors',
        'hover:bg-white/10 disabled:opacity-40 disabled:hover:bg-transparent'
      )}
    >
      {children}
    </button>
  );
}

export default BulkActionBar;
